/**
 * Test ID convention for dst components.
 *
 * Reusable components (form widgets, list rows, etc.) that want to be
 * driveable by Playwright / Jest accept an optional `testIdPrefix?: string`
 * prop. When set, they tag their key elements following this convention:
 *
 *   <prefix>-input        — primary input or focusable trigger
 *   <prefix>-option-<id>  — items in a dynamic list (use a stable id, never the index)
 *   <prefix>-<sub-role>   — auxiliary controls (e.g. "calling-code", "submit", "clear")
 *
 * Caller picks the prefix from a feature / surface domain. Same widget can
 * appear in multiple places with distinct prefixes:
 *
 *   <PopularHierarchicalLocationSelect testIdPrefix="signup-location" />
 *   <PopularHierarchicalLocationSelect testIdPrefix="profile-location" />
 *
 * Why a prop and not a hard-coded testid in the widget: reusable widgets
 * (location pickers, phone inputs) appear in N surfaces. A hard-coded
 * testid duplicates across surfaces and makes test selectors ambiguous.
 *
 * Why optional: most production renders don't need a testid. The prop
 * stays undefined and no data-testid is emitted, keeping the DOM clean.
 *
 * Convention reminder for component authors:
 *
 *   const Widget = ({ testIdPrefix, ...rest }) => (
 *     <input data-testid={testIdPrefix && `${testIdPrefix}-input`} ... />
 *   );
 *
 * The `testIdPrefix &&` guard means undefined prefix → no attribute at all
 * (rather than emitting `data-testid="undefined-input"`).
 */

/**
 * Helper for components that build a list of options with stable ids.
 *
 *   <Option data-testid={optionTestId(testIdPrefix, location.id)} />
 */
export function optionTestId(
  prefix: string | undefined,
  id: string | number
): string | undefined {
  return prefix ? `${prefix}-option-${id}` : undefined;
}

/**
 * Helper for primary input fields.
 */
export function inputTestId(prefix: string | undefined): string | undefined {
  return prefix ? `${prefix}-input` : undefined;
}

/**
 * Helper for auxiliary sub-controls (calling-code, clear, submit, etc.).
 */
export function subTestId(
  prefix: string | undefined,
  subRole: string
): string | undefined {
  return prefix ? `${prefix}-${subRole}` : undefined;
}
